import jwtDecode from 'jwt-decode';
import { isMongoIdString } from './impersonationContext';

type JwtPayload = {
  role?: string;
  isAdmin?: boolean;
};

/**
 * Client-side admin gate for **routing** (JWT + localStorage), aligned with
 * `AdminLayout` and `RoleDashboardRouter`. Artist/streamer JWT roles must not
 * be overridden by stale `isAdmin` / `admin` flags.
 *
 * SuperAdmin impersonation keeps the admin JWT but sets `impersonating` +
 * `impersonatedArtistId`; for `/dashboard` routing we must treat that as the
 * artist portal, not the admin shell.
 */
export function resolveClientAdminSession(routingToken: string | null): boolean {
  try {
    if (typeof localStorage !== 'undefined') {
      if (localStorage.getItem('impersonating') === 'true') {
        const id = (localStorage.getItem('impersonatedArtistId') || '').trim();
        if (isMongoIdString(id)) return false;
      }
    }

    const lsAdmin =
      localStorage.getItem('isAdmin') === 'true' || localStorage.getItem('admin') === 'true';
    const token = routingToken || localStorage.getItem('token');
    if (!token) return lsAdmin;

    const normalized = token.startsWith('Bearer ') ? token.slice(7).trim() : token;
    const decoded = jwtDecode<JwtPayload>(normalized);
    const role = (decoded.role || '').toLowerCase();
    if (role === 'user' || role === 'artist') {
      return false;
    }
    if (decoded.role === 'admin' || decoded.isAdmin === true) return true;
    return lsAdmin;
  } catch {
    return localStorage.getItem('isAdmin') === 'true' || localStorage.getItem('admin') === 'true';
  }
}
